import {
  AmbientLight,
  DirectionalLight,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PointLight,
  Scene,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export interface SceneBundle {
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly renderer: WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly accent: PointLight;
  readonly controls: OrbitControls;
}

const FOV = 45;

export const createSceneBundle = (parent: HTMLElement): SceneBundle => {
  const canvas = document.createElement('canvas');
  canvas.id = 'gameCanvas';
  parent.appendChild(canvas);

  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;

  const scene = new Scene();

  const camera = new PerspectiveCamera(FOV, 1, 0.1, 200);
  camera.position.set(0, 0, 16);
  camera.lookAt(0, 0, 0);
  scene.background = null;

  const ambient = new AmbientLight('#ffffff', 0.65);
  scene.add(ambient);

  const sun = new DirectionalLight('#ffffff', 1.4);
  sun.position.set(6, 8, 14);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 60;
  sun.shadow.camera.left = -18;
  sun.shadow.camera.right = 18;
  sun.shadow.camera.top = 18;
  sun.shadow.camera.bottom = -18;
  sun.shadow.bias = -0.0008;
  scene.add(sun);

  const rim = new DirectionalLight('#dde6ff', 0.55);
  rim.position.set(-8, -4, 6);
  scene.add(rim);

  const accent = new PointLight('#ffffff', 0, 12, 2);
  accent.position.set(0, 6, 0);
  scene.add(accent);

  const controls = new OrbitControls(camera, canvas);
  controls.target.set(0, 0, 0);
  controls.enablePan = false;
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.rotateSpeed = 0.7;
  controls.zoomSpeed = 0.9;
  controls.minDistance = 8;
  controls.maxDistance = 40;
  controls.minPolarAngle = Math.PI * 0.18;
  controls.maxPolarAngle = Math.PI - Math.PI * 0.18;
  controls.minAzimuthAngle = -Math.PI * 0.7;
  controls.maxAzimuthAngle = Math.PI * 0.7;
  controls.touches = { ONE: 0, TWO: 2 };

  return { scene, camera, renderer, canvas, accent, controls };
};

export const fitCameraToBoard = (bundle: SceneBundle, boardSize: number, cellSize: number): void => {
  const span = boardSize * cellSize;
  const vFov = (bundle.camera.fov * Math.PI) / 180;
  const aspect = bundle.camera.aspect || 1;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
  const minFov = Math.min(vFov, hFov);
  const distance = ((span / 2) * 1.18) / Math.tan(minFov / 2);
  bundle.camera.position.set(0, 0, distance);
  bundle.controls.target.set(0, 0, 0);
  bundle.controls.minDistance = distance * 0.55;
  bundle.controls.maxDistance = distance * 2.4;
  bundle.controls.update();
};

export const handleResize = (parent: HTMLElement, bundle: SceneBundle): void => {
  const w = parent.clientWidth;
  const h = parent.clientHeight;
  if (w === 0 || h === 0) return;
  bundle.renderer.setSize(w, h, false);
  bundle.camera.aspect = w / h;
  bundle.camera.updateProjectionMatrix();
};
